// Badge definitions — thresholds, icons, display info, rewards
// Badges are evaluated against the aggregated profile stats.
// Only add new badges; never remove or rename existing IDs (users have earned them).

export const BADGE_CATEGORIES = {
  explorer:    { label: 'Explorer',    color: '#D4A574' },
  challenger:  { label: 'Challenger',  color: '#8B7355' },
  storyteller: { label: 'Storyteller', color: '#B8A082' },
  globetrotter:{ label: 'Globetrotter',color: '#6B8E6B' },
}

// condition: function that receives profile.stats and returns true if earned
export const BADGES = [
  // ── Explorer (points-based) ────────────────────────────────────────────────
  {
    id: 'wanderer',
    category: 'explorer',
    name: 'Wanderer',
    icon: '🌱',
    description: 'Earn your first points',
    reward: 'Profile unlocked',
    condition: s => s.totalPoints >= 10,
  },
  {
    id: 'bronze_explorer',
    category: 'explorer',
    name: 'Bronze Explorer',
    icon: '🥉',
    description: 'Reach 75 points',
    reward: '5% discount on next challenge prize',
    condition: s => s.totalPoints >= 75,
  },
  {
    id: 'silver_explorer',
    category: 'explorer',
    name: 'Silver Explorer',
    icon: '🥈',
    description: 'Reach 200 points',
    reward: '10% discount on next challenge prize',
    condition: s => s.totalPoints >= 200,
  },
  {
    id: 'gold_explorer',
    category: 'explorer',
    name: 'Gold Explorer',
    icon: '🥇',
    description: 'Reach 450 points',
    reward: 'Priority challenge review',
    condition: s => s.totalPoints >= 450,
  },

  // ── Challenger ─────────────────────────────────────────────────────────────
  {
    id: 'first_challenge',
    category: 'challenger',
    name: 'Challenge Accepted',
    icon: '🎯',
    description: 'Complete your first challenge',
    reward: 'Challenges badge on profile',
    condition: s => s.challengesCompleted >= 1,
  },
  {
    id: 'challenge_hunter',
    category: 'challenger',
    name: 'Challenge Hunter',
    icon: '🏹',
    description: 'Complete 5 challenges',
    reward: 'Hunter frame on profile',
    condition: s => s.challengesCompleted >= 5,
  },
  {
    id: 'challenge_master',
    category: 'challenger',
    name: 'Challenge Master',
    icon: '🏆',
    description: 'Complete 15 challenges',
    reward: '15% discount on all challenge prizes',
    condition: s => s.challengesCompleted >= 15,
  },

  // ── Storyteller (memories + photos) ───────────────────────────────────────
  {
    id: 'first_memory',
    category: 'storyteller',
    name: 'First Memory',
    icon: '📖',
    description: 'Write your first memory',
    reward: 'Memory badge on profile',
    condition: s => s.memoriesWritten >= 1,
  },
  {
    id: 'chronicler',
    category: 'storyteller',
    name: 'Chronicler',
    icon: '✍️',
    description: 'Write 10 memories',
    reward: 'Chronicler title on profile',
    condition: s => s.memoriesWritten >= 10,
  },
  {
    id: 'photographer',
    category: 'storyteller',
    name: 'Eye for Detail',
    icon: '📷',
    description: 'Upload 10 photos across your trips',
    reward: 'Photo frame unlock',
    condition: s => s.photosUploaded >= 10,
  },

  // ── Globetrotter (trips / destinations) ───────────────────────────────────
  {
    id: 'first_trip',
    category: 'globetrotter',
    name: 'First Adventure',
    icon: '✈️',
    description: 'Complete your first trip',
    reward: 'Explorer status',
    condition: s => s.tripsTotal >= 1,
  },
  {
    id: 'globetrotter',
    category: 'globetrotter',
    name: 'Globetrotter',
    icon: '🌍',
    description: 'Visit 3 different countries',
    reward: 'Globetrotter badge on profile',
    condition: s => s.countriesVisited.length >= 3,
  },
]

// Level thresholds — index = level number (1-based)
export const LEVELS = [
  null,                                           // index 0 unused
  { label: 'Curious Wanderer',  minPts: 0   },   // level 1
  { label: 'Explorer',          minPts: 50  },   // level 2
  { label: 'Adventurer',        minPts: 150 },   // level 3
  { label: 'Seasoned Traveller',minPts: 350 },   // level 4
  { label: 'World Citizen',     minPts: 700 },   // level 5
]

export function getLevel(totalPoints) {
  let level = 1
  for (let i = LEVELS.length - 1; i >= 1; i--) {
    if (totalPoints >= LEVELS[i].minPts) { level = i; break }
  }
  const current = LEVELS[level]
  const next = LEVELS[level + 1] || null
  const ptsIntoLevel = totalPoints - current.minPts
  const ptsToNext = next ? next.minPts - current.minPts : null
  const progress = next ? Math.min(1, ptsIntoLevel / ptsToNext) : 1
  return { level, label: current.label, progress, ptsToNext, ptsIntoLevel, isMax: !next }
}
